import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import { DataService } from 'src/app/data.service';

/**
 *  ÓRAREND-IMPORT ÉS TAKARÍTÁS — a „Nem naplózott tanórák" lista betöltése.
 *
 *  Két művelet, mert egy munkafolyamat két fele. Ha az órarend rosszul került be —
 *  tipikusan úgy, hogy a csoportok még nem léteztek, tehát minden óra csoport nélkül,
 *  osztályszinten landolt —, akkor előbb takarítani kell, aztán újra betölteni.
 *
 *  A SORREND SZÁMÍT: előbb a CSOPORTOKAT kell importálni. Az „Osztály/csoport" oszlop
 *  ugyanis kétféle: vagy osztálynév („11/G"), vagy csoportnév („9/fg/1/tagozat").
 *  Egy éles fájlban 554 sorból 335 nem talált célt, amíg a csoportok nem léteztek —
 *  utána egy sem maradt párosítatlanul.
 */
@Component({
  selector: 'app-admin-importorarend',
  templateUrl: './importorarend.component.html',
  styleUrls: ['./importorarend.component.scss'],
})
export class AdminImportOrarendComponent implements OnInit {
  intezmeny = '';
  ev: number | null = null;

  fajlNev = '';
  fejlecek: string[] = [];
  nyersSorok: any[] = [];
  oszlop = { datum: '', ora: '', osztaly: '', tantargy: '', tanar: '', helyettesito: '' };

  elemzes: any = null;
  elemzesFut = false;
  fut = false;
  eredmeny: any = null;

  termek: any[] = [];
  terem = '';
  tantargyLetrehoz = false;
  /** Csak a hibás sorokat mutatjuk alapból — a jó sorok nem igényelnek figyelmet. */
  csakHibas = true;

  /*  ── ELLENŐRZÉS ÉS JAVÍTÁS ────────────────────────────────────────────────
   *  Egy import után az órarend „üresnek" látszhat, pedig a sorok ott vannak: a
   *  képernyők kötéseken keresztül olvassák az órát, és ami nem talál célt, az nem
   *  jelenik meg. Ez a rész megmutatja, mi hiányzik, és javítja — újraimportálás nélkül. */
  ellenorzes: any = null;
  ellenorzesFut = false;
  javitasFut = false;
  javitasEredmeny: any = null;
  javit = {
    csoport_osztaly_nevbol: true,
    tanar_username: true,
    duplikatum: true,
    osztaly_csoportbol: true,
    tanev: true,
    tantargy_kerettantervrol: false,
    intezmeny_atkot: false,
  };

  // ── Takarítás ──────────────────────────────────────────────────────────────
  tol = '';
  ig = '';
  torlesElonezet: any = null;
  torlesFut = false;
  naplozottatIs = false;
  torlesEredmeny: any = null;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    try {
      this.ev = Number(JSON.parse(await this.data.getFromStorage('tanev')).aktualistanev) || null;
    } catch { this.ev = null; }
    if (this.ev) this.termekBetoltese();
  }

  get intezmenyPath(): string { return encodeURI(this.intezmeny); }

  async termekBetoltese() {
    try {
      this.termek = await firstValueFrom(this.http.get<any[]>(
        `/api/nyilvantartas/tanterem/${this.ev}/${this.intezmenyPath}`)) || [];
    } catch { this.termek = []; }
  }

  /* ── Fájl ────────────────────────────────────────────────────────────── */
  private static readonly JELOLTEK: Record<string, string[]> = {
    datum: ['dátum', 'datum', 'nap dátuma'],
    ora: ['óraszám', 'oraszam', 'óra', 'sorszám'],
    osztaly: ['osztály/csoport', 'osztaly/csoport', 'osztály', 'csoport'],
    tantargy: ['tantárgy', 'tantargy'],
    tanar: ['tanár', 'tanar', 'pedagógus'],
    helyettesito: ['helyettesítő tanár', 'helyettesito tanar', 'helyettesítő'],
  };

  fajlValtozott(event: any) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.fajlNev = file.name;
    this.elemzes = null; this.eredmeny = null;
    const olvaso = new FileReader();
    olvaso.onload = (e: any) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const sorok: any[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]],
          { raw: false, defval: null });
        this.nyersSorok = sorok;
        this.fejlecek = sorok.length ? Object.keys(sorok[0]) : [];
        for (const [mezo, jeloltek] of Object.entries(AdminImportOrarendComponent.JELOLTEK)) {
          (this.oszlop as any)[mezo] = this.fejlecek.find((f) =>
            jeloltek.includes(String(f).trim().toLowerCase()))
            || this.fejlecek.find((f) => jeloltek.some((j) => String(f).trim().toLowerCase().includes(j)))
            || '';
        }
        if (!sorok.length) this.data.sendToast('A fájl üres.', 'warning');
      } catch {
        this.data.sendToast('A fájlt nem sikerült beolvasni. Excel (.xlsx/.xls) vagy CSV kell.', 'error');
        this.nyersSorok = []; this.fejlecek = [];
      }
    };
    olvaso.readAsBinaryString(file);
  }

  get kuldhetoSorok(): any[] {
    if (!this.oszlop.datum || !this.oszlop.osztaly) return [];
    const e = (r: any, o: string) => (o ? String(r[o] ?? '').trim() : '');
    return (this.nyersSorok || []).map((r) => ({
      datum: e(r, this.oszlop.datum),
      ora: e(r, this.oszlop.ora),
      osztaly: e(r, this.oszlop.osztaly),
      tantargy: e(r, this.oszlop.tantargy),
      tanar: e(r, this.oszlop.tanar),
      helyettesito: e(r, this.oszlop.helyettesito),
    })).filter((r) => r.datum && r.osztaly);
  }

  /* ── Elemzés / import ────────────────────────────────────────────────── */

  async elemez() {
    if (!this.ev) { this.data.sendToast('Nincs tanév.', 'warning'); return; }
    const sorok = this.kuldhetoSorok;
    if (!sorok.length) {
      this.data.sendToast('Nincs feldolgozható sor — állítsd be a dátum és az osztály/csoport oszlopát.', 'warning');
      return;
    }
    this.elemzesFut = true; this.eredmeny = null;
    try {
      this.elemzes = await firstValueFrom(this.http.post(
        `/api/orarend-import/elemzes/${this.intezmenyPath}`, { sorok, ev: this.ev }));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az elemzés nem sikerült.', 'error');
      this.elemzes = null;
    } finally { this.elemzesFut = false; }
  }

  get mutatottSorok(): any[] {
    const s = this.elemzes?.sorok || [];
    return this.csakHibas ? s.filter((x: any) => x.allapot === 'hibas') : s;
  }

  async importal() {
    if (!this.terem) { this.data.sendToast('Válassz termet — az órarendben kötelező mező.', 'warning'); return; }
    const o = this.elemzes?.osszegzes;
    const uzenet = [
      `${o.felviheto} óra kerül be a(z) ${this.ev}/${(this.ev || 0) + 1}. tanévbe,`,
      `${(o.napok || []).length} napra (${(o.napok || [])[0]} – ${(o.napok || []).slice(-1)[0]}).`,
      ...(o.mar_bent ? [`${o.mar_bent} óra már bent van, azt kihagyjuk.`] : []),
      ...(o.hibas ? [`${o.hibas} sor hibás, azok NEM kerülnek be.`] : []),
      ...(this.tantargyLetrehoz && this.elemzes?.hianyzo_tantargy?.length
        ? ['', `Létrejön ${this.elemzes.hianyzo_tantargy.length} új tantárgy is.`] : []),
      '', 'Folytatod?',
    ].join('\n');
    if (!confirm(uzenet)) return;

    this.fut = true;
    try {
      this.eredmeny = await firstValueFrom(this.http.post(
        `/api/orarend-import/vegrehajt/${this.intezmenyPath}`,
        { sorok: this.kuldhetoSorok, ev: this.ev, terem: this.terem,
          tantargy_letrehoz: this.tantargyLetrehoz }));
      this.elemzes = this.eredmeny?.allapot || this.elemzes;
      this.data.sendToast(`${this.eredmeny.felvive} óra felvive.`, 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az import nem futott le.', 'error');
    } finally { this.fut = false; }
  }

  /* ── Termek pótlása ──────────────────────────────────────────────────────
   *  Az órarendben a terem kötelező mező, a betöltésre használt lista viszont nem
   *  tartalmazza — az import ezért mindent EGY terembe tesz. Ez a művelet egy olyan
   *  táblából pótolja (aSc órarend-kiírás vagy bármely export, amiben van terem),
   *  ahol az óra mellett ott a terem is. */
  tFajlNev = '';
  tFejlecek: string[] = [];
  tNyers: any[] = [];
  tOszlop = { nap: '', ora: '', osztaly: '', tantargy: '', tanar: '', terem: '' };
  /** „Gyűjtő" terem: az import ide tett mindent, csak ebből írunk át. */
  gyujtoTerem = '';
  tElemzes: any = null;
  tElemzesFut = false;
  tFut = false;
  tEredmeny: any = null;

  private static readonly T_JELOLTEK: Record<string, string[]> = {
    nap: ['hét napja', 'nap', 'dátum', 'datum'],
    ora: ['óraszám', 'oraszam', 'óra', 'sorszám'],
    osztaly: ['osztály/csoport', 'osztály', 'osztaly', 'csoport'],
    tantargy: ['tantárgy', 'tantargy'],
    tanar: ['tanár', 'tanar', 'pedagógus'],
    terem: ['terem', 'tanterem', 'helyiség', 'helyiseg'],
  };

  tFajlValtozott(event: any) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.tFajlNev = file.name;
    this.tElemzes = null; this.tEredmeny = null;
    const olvaso = new FileReader();
    olvaso.onload = (e: any) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const sorok: any[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]],
          { raw: false, defval: null });
        this.tNyers = sorok;
        this.tFejlecek = sorok.length ? Object.keys(sorok[0]) : [];
        for (const [mezo, jeloltek] of Object.entries(AdminImportOrarendComponent.T_JELOLTEK)) {
          (this.tOszlop as any)[mezo] = this.tFejlecek.find((f) =>
            jeloltek.includes(String(f).trim().toLowerCase()))
            || this.tFejlecek.find((f) => jeloltek.some((j) => String(f).trim().toLowerCase().includes(j)))
            || '';
        }
      } catch {
        this.data.sendToast('A fájlt nem sikerült beolvasni.', 'error');
        this.tNyers = []; this.tFejlecek = [];
      }
    };
    olvaso.readAsBinaryString(file);
  }

  get tKuldheto(): any[] {
    if (!this.tOszlop.nap || !this.tOszlop.osztaly || !this.tOszlop.terem) return [];
    const e = (r: any, o: string) => (o ? String(r[o] ?? '').trim() : '');
    return (this.tNyers || []).map((r) => ({
      nap: e(r, this.tOszlop.nap), ora: e(r, this.tOszlop.ora),
      osztaly: e(r, this.tOszlop.osztaly), tantargy: e(r, this.tOszlop.tantargy),
      tanar: e(r, this.tOszlop.tanar), terem: e(r, this.tOszlop.terem),
    })).filter((r) => r.osztaly && r.terem);
  }

  private get tKeres(): any {
    return { sorok: this.tKuldheto, ev: this.ev, tol: this.tol, ig: this.ig,
      csak_terem: this.gyujtoTerem || undefined };
  }

  async teremElemez() {
    if (!this.tol || !this.ig) { this.data.sendToast('Adj meg időszakot (fent).', 'warning'); return; }
    if (!this.tKuldheto.length) {
      this.data.sendToast('Állítsd be legalább a nap, az osztály/csoport és a terem oszlopát.', 'warning');
      return;
    }
    this.tElemzesFut = true; this.tEredmeny = null;
    try {
      this.tElemzes = await firstValueFrom(this.http.post(
        `/api/orarend-import/terem-elemzes/${this.intezmenyPath}`, this.tKeres));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az elemzés nem sikerült.', 'error');
      this.tElemzes = null;
    } finally { this.tElemzesFut = false; }
  }

  async teremPotol() {
    const o = this.tElemzes?.osszegzes;
    if (!o) return;
    const uzenet = [
      `${o.erintett_ora} óra terme íródik át (${this.tol} – ${this.ig}).`,
      this.gyujtoTerem
        ? 'Csak a kiválasztott gyűjtő teremben lévő órákhoz nyúlunk.'
        : 'FIGYELEM: nincs gyűjtő terem megadva — a kézzel beállított, helyes termeket is felülírhatja.',
      ...(o.fedetlen_ora ? ['', `${o.fedetlen_ora} órához nem találunk termet — ezeket a lista mutatja.`] : []),
      '', 'Folytatod?',
    ].join('\n');
    if (!confirm(uzenet)) return;
    this.tFut = true;
    try {
      this.tEredmeny = await firstValueFrom(this.http.post(
        `/api/orarend-import/terem-potlas/${this.intezmenyPath}`, this.tKeres));
      this.tElemzes = this.tEredmeny?.allapot || this.tElemzes;
      this.data.sendToast(`${this.tEredmeny.modositott} óra terme pótolva.`, 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A pótlás nem sikerült.', 'error');
    } finally { this.tFut = false; }
  }

  /* ── Takarítás: csoport nélküli órák ─────────────────────────────────── */

  async torlestNez() {
    if (!this.tol || !this.ig) { this.data.sendToast('Adj meg kezdő és záró dátumot.', 'warning'); return; }
    this.torlesEredmeny = null;
    try {
      this.torlesElonezet = await firstValueFrom(this.http.post(
        `/api/orarend-import/torles-elonezet/${this.intezmenyPath}`, { tol: this.tol, ig: this.ig }));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az előnézet nem sikerült.', 'error');
    }
  }

  async torol() {
    const e = this.torlesElonezet;
    if (!e) return;
    const db = this.naplozottatIs ? e.osszes : e.torolheto;
    const uzenet = [
      `${db} csoport nélküli óra törlése: ${e.tol} – ${e.ig}.`,
      '',
      ...(this.naplozottatIs
        ? ['FIGYELEM: a MÁR NAPLÓZOTT órákat is törlöd. A bennük lévő hiányzás,',
           'igazolás és dicséret ELVÉSZ — az óra újra felvihető, a beírt adat nem.']
        : [`${e.naplozott} már naplózott óra érintetlen marad.`]),
      `${e.erintetlen_csoportos} csoportos órához nem nyúlunk.`,
      '',
      'Ez nem vonható vissza. Folytatod?',
    ].join('\n');
    if (!confirm(uzenet)) return;

    this.torlesFut = true;
    try {
      this.torlesEredmeny = await firstValueFrom(this.http.post(
        `/api/orarend-import/torles/${this.intezmenyPath}`,
        { tol: this.tol, ig: this.ig, naplozottat_is: this.naplozottatIs }));
      this.torlesElonezet = this.torlesEredmeny?.allapot || this.torlesElonezet;
      this.data.sendToast(`${this.torlesEredmeny.torolve} óra törölve.`, 'success');
    } catch (e2: any) {
      this.data.sendToast(e2?.error?.error || 'A törlés nem sikerült.', 'error');
    } finally { this.torlesFut = false; }
  }

  /** Mit lát – és mit nem lát – a rendszer az adott időszak óráiból. */
  async ellenorzestFuttat() {
    if (!this.tol || !this.ig) { this.data.sendToast('Adj meg időszakot.', 'warning'); return; }
    this.ellenorzesFut = true;
    this.javitasEredmeny = null;
    try {
      this.ellenorzes = await firstValueFrom(this.http.post(
        `/api/orarend-import/ellenorzes/${this.intezmenyPath}`, { tol: this.tol, ig: this.ig }));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az ellenőrzés nem sikerült.', 'error');
    } finally {
      this.ellenorzesFut = false;
    }
  }

  get javitandoMuveletek(): string[] {
    return Object.entries(this.javit).filter(([, v]) => v).map(([k]) => k);
  }

  async javitastFuttat() {
    if (!this.ellenorzes) return;
    if (!this.javitandoMuveletek.length) { this.data.sendToast('Nincs kijelölt művelet.', 'warning'); return; }
    if (!confirm('A kijelölt javítások lefutnak a megadott időszakra.\n\n'
      + 'Órát nem hozunk létre és nem törlünk — csak a kötéseket igazítjuk '
      + '(csoport–osztály, tanév, tantárgy, intézmény).\n\nFolytatod?')) return;

    this.javitasFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/orarend-import/javitas/${this.intezmenyPath}`,
        { tol: this.tol, ig: this.ig, muveletek: this.javit }));
      this.javitasEredmeny = r.eredmeny;
      this.ellenorzes = r.allapot;
      this.data.sendToast('A javítás lefutott.', 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A javítás nem sikerült.', 'error');
    } finally {
      this.javitasFut = false;
    }
  }
}